import { useState, useEffect, useCallback } from 'react';
import { sitesApi, subscriptionsApi } from '../../api/client';
import { createPreviewApiClient } from './apiClient';
import type { WebsitePreviewData } from './types';
import { INITIAL_PREVIEW_DATA } from './types';
import type { Component, Page, Site, PageLayout } from '../../schemas/validation';
import { resolveSiteThemeForPreview } from '../../utils/resolveSiteThemeForPreview';

export function useWebsitePreviewData(siteId: string | undefined, currentSlug: string) {
  const [data, setData] = useState<WebsitePreviewData>({ ...INITIAL_PREVIEW_DATA });

  const fetchWebsitePreviewData = useCallback(async () => {
    if (!siteId) return;

    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      const apiClient = createPreviewApiClient();
      let themeColors: any = null;
      let websiteData: any = null;
      let useOldApproach = false;

      console.log('🔍 Website Preview: Loading data for:', {
        siteId,
        slug: currentSlug,
        apiBaseUrl: apiClient.defaults.baseURL,
      });

      // Try new preview API first
      try {
        const previewUrl = currentSlug
          ? `/sites/${siteId}/preview/page/${currentSlug}`
          : `/sites/${siteId}/preview`;

        console.log('🔍 Website Preview: Trying new API:', previewUrl);
        const response = await apiClient.get(previewUrl);
        websiteData = response.data.success ? response.data.data : response.data;

        if (!websiteData.page) {
          console.warn('🔍 Website Preview: New API returned no page data, falling back');
          useOldApproach = true;
          websiteData = null;
        }
      } catch (newApiError: any) {
        console.warn('🔍 Website Preview: New API failed:', newApiError.response?.status);
        if (newApiError.response?.status === 404) {
          useOldApproach = true;
        } else {
          throw newApiError;
        }
      }

      // Fall back to old approach if needed
      if (useOldApproach) {
        websiteData = await fetchWithOldApproach(siteId, currentSlug, apiClient);
        themeColors = websiteData.themeColors;
      } else {
        // Parse components from new API response
        websiteData.components = parsePageComponents(websiteData.page?.components);

        // Prefer server theme (includes brand package); fall back like PagePreview
        themeColors = await resolveSiteThemeForPreview({
          serverTheme: websiteData.theme,
          site: websiteData.site,
          fetchThemeByName: themeName => loadThemeColors(apiClient, themeName),
        });
      }

      // Parse layout
      const layout = parseLayout(websiteData.layout);

      // Check subscription to determine if branding should show
      let showBranding = false;
      try {
        console.log('🔍 Website Preview: Checking subscription for branding...');
        const response = await subscriptionsApi.getCurrentSubscription();
        console.log(
          '🔍 Website Preview: Subscription response:',
          JSON.stringify(response, null, 2)
        );
        // API returns { subscription: {...} | null, usage: { plan: { name: '...' } } }
        // Check the nested subscription object, or fall back to usage.plan
        const planName = response.subscription?.planName || response.usage?.plan?.name;
        console.log('🔍 Website Preview: Extracted planName:', planName);
        // Show branding if no plan or on FREE plan
        showBranding = !planName || planName.toUpperCase() === 'FREE';
        console.log('🔍 Website Preview: showBranding set to:', showBranding);
      } catch (subError) {
        console.warn(
          '🔍 Website Preview: Could not check subscription, defaulting to show branding:',
          subError
        );
        showBranding = true;
      }

      console.log('🔍 Website Preview: Final data:', {
        site: websiteData.site,
        page: websiteData.page?.title,
        layout: layout ? { name: layout.name } : null,
        showBranding,
      });

      setData({
        website: websiteData.site,
        page: websiteData.page,
        components: websiteData.components,
        navigation: websiteData.navigation || [],
        theme: themeColors || websiteData.theme || null,
        layout,
        loading: false,
        error: null,
        showBranding,
      });
    } catch (err: any) {
      console.error('🔍 Website Preview: Failed to load data:', err);
      const errorMessage = getErrorMessage(err);
      setData(prev => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [siteId, currentSlug]);

  useEffect(() => {
    if (!siteId) return;

    const authToken = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');

    if (!authToken || !user) {
      setData(prev => ({
        ...prev,
        loading: false,
        error: 'Please log in to preview websites',
      }));
      return;
    }

    fetchWebsitePreviewData();
  }, [siteId, currentSlug, fetchWebsitePreviewData]);

  return { data, refetch: fetchWebsitePreviewData };
}

async function fetchWithOldApproach(
  siteId: string,
  currentSlug: string,
  apiClient: ReturnType<typeof createPreviewApiClient>
) {
  console.log('🔍 Website Preview: Using old API approach');

  let site: Site;
  let pages: Page[] = [];
  let themeColors: any = null;

  try {
    const sites = await sitesApi.list();
    const foundSite = sites.find(s => s.id === siteId);
    if (!foundSite) throw new Error('Site not found');
    site = foundSite;
    pages = await sitesApi.getPages(siteId);
  } catch {
    console.warn('🔍 Website Preview: API unavailable, using localStorage');
    const result = getDataFromLocalStorage(siteId);
    site = result.site;
    pages = result.pages;
  }

  // Find target page
  const targetPage = findTargetPage(pages, currentSlug);
  if (!targetPage) throw new Error('No pages found for this website');

  // Get components
  const components = await getPageComponents(apiClient, targetPage);

  // Build navigation
  const navigation = pages
    .filter(p => !p.slug || p.slug !== 'admin')
    .sort((a, b) => (a.orderPosition || 0) - (b.orderPosition || 0))
    .map(p => ({
      title: p.title,
      slug: p.slug || '',
      orderPosition: p.orderPosition || 0,
    }));

  themeColors = await resolveSiteThemeForPreview({
    site,
    fetchThemeByName: themeName => loadThemeColors(apiClient, themeName),
  });

  return {
    site,
    page: targetPage,
    components,
    navigation,
    theme: themeColors || 'standard',
    themeColors,
  };
}

function getDataFromLocalStorage(siteId: string): { site: Site; pages: Page[] } {
  const sitesData = localStorage.getItem('sites');
  let site: Site;

  if (sitesData) {
    const sites = JSON.parse(sitesData);
    site = sites.find((s: Site) => s.id === siteId);
    if (!site) throw new Error('Site not found in local storage');
  } else {
    site = {
      id: siteId,
      privacyBannerEnabled: false,
      userId: 'mock-user',
      name: 'Website Preview',
      theme: 'standard',
      isPublished: false,
      isLandingPage: false,
      nativeAnalyticsEnabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  const localPages = localStorage.getItem(`pages-${siteId}`);
  const pages: Page[] = localPages
    ? JSON.parse(localPages)
    : [
        {
          id: 'mock-homepage',
          siteId,
          title: 'Home',
          slug: '',
          components: JSON.stringify({ ROOT: { type: 'div', nodes: [] } }),
          orderPosition: 0,
          isHomePage: true,
          showLayout: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ];

  return { site, pages };
}

function findTargetPage(pages: Page[], currentSlug: string): Page | undefined {
  if (currentSlug) {
    return pages.find(p => p.slug === currentSlug) || pages.find(p => p.isHomePage) || pages[0];
  }
  return pages.find(p => p.isHomePage === true) || pages[0];
}

async function getPageComponents(
  apiClient: ReturnType<typeof createPreviewApiClient>,
  targetPage: Page
): Promise<Component[]> {
  try {
    const response = await apiClient.get<{ components: Component[] }>(
      `/pages/${targetPage.id}/components`
    );
    return sortAndFilterComponents(response.data.components);
  } catch {
    console.warn('🔍 Website Preview: Failed to get components from API');
    return sortAndFilterComponents(parsePageComponents(targetPage.components));
  }
}

function parsePageComponents(components: any): Component[] {
  if (!components) return [];

  try {
    const parsed = typeof components === 'string' ? JSON.parse(components) : components;

    if (parsed.ROOT) return [];
    if (Array.isArray(parsed)) return parsed;
    if (parsed.components && Array.isArray(parsed.components)) return parsed.components;
    return [];
  } catch (error) {
    console.error('Error parsing components:', error);
    return [];
  }
}

function sortAndFilterComponents(components: Component[]): Component[] {
  return components
    .filter(component => {
      if (!component.visible) return false;
      if (!component.id || !component.type || !component.data) {
        console.warn('Invalid component found, skipping:', component);
        return false;
      }
      return true;
    })
    .sort((a, b) => a.order - b.order);
}

async function loadThemeColors(
  apiClient: ReturnType<typeof createPreviewApiClient>,
  themeName: string
): Promise<any> {
  try {
    const response = await apiClient.get(`/themes/${themeName}`);
    if (response.data) {
      console.log('🎨 WebsitePreview: Loaded theme colors for', themeName);
      return response.data.customColors;
    }
  } catch (themeError) {
    console.warn('Failed to load theme colors:', themeError);
  }
  return null;
}

function parseLayout(layoutData: any): PageLayout | null {
  if (!layoutData) return null;

  let layout = layoutData;
  if (layout.components && typeof layout.components === 'string') {
    try {
      const parsedComponents = JSON.parse(layout.components);
      layout = {
        ...layout,
        components: parsedComponents.components || parsedComponents || [],
      };
    } catch (e) {
      console.error('Error parsing layout components:', e);
    }
  }
  return layout;
}

function getErrorMessage(err: any): string {
  if (err.response) {
    const status = err.response.status;
    if (status === 401) return 'Authentication failed. Please log in again.';
    if (status === 404) return 'Website or page not found.';
    if (status === 500) return 'Server error. Please try again later.';
  }
  return 'Failed to load website preview';
}
